import _ from 'underscore';
import {ColumnSchema, GridEditor} from './types';
import {getColumnsFromGrid} from './utils';

const COLUMN_WIDTHS = [75, 100, 250] as const;

/** Enforce minimum width */
const getWidth = (gridEditor: GridEditor) => (x: number) => {
	const column = gridEditor.behavior.getActiveColumn(x);
	const gridProperties = gridEditor.properties;
	const minWidth = gridProperties.minimumColumnWidth;

	return !column
		? gridProperties.defaultColumnWidth
		: Math.max(minWidth, column.getWidth());
};

/** Enforce minimum width */
const setWidth =
	(gridEditor: GridEditor): GridEditor['setColumnWidth'] =>
	(columnIndex, columnWidth) => {
		gridEditor.behavior.setColumnWidth(
			columnIndex,
			Math.max(columnWidth, gridEditor.properties.minimumColumnWidth),
		);
	};

/** Set column initial widths */
const setWidths =
	(gridEditor: GridEditor) =>
	(columnWidths: number[] = []) => {
		const gridProperties = gridEditor.properties;
		const minWidth = gridProperties.minimumColumnWidth;

		// Index column
		gridEditor.setColumnWidth(-2, minWidth);

		getColumnsFromGrid(gridEditor).forEach((column, idx) => {
			const {properties} = column;

			// Defined column widths
			if (columnWidths[idx]) {
				gridEditor.setColumnWidth(idx, columnWidths[idx]);
				return;
			} else if (COLUMN_WIDTHS[idx]) {
				gridEditor.setColumnWidth(idx, COLUMN_WIDTHS[idx]);
				return;
			}

			// Try and set based on column type
			switch (properties.type) {
				case 'date':
					gridEditor.setColumnWidth(idx, 160);
					break;
				case 'multiselect':
				case 'select':
					gridEditor.setColumnWidth(idx, 200);
					break;
				case 'semantic':
				case 'tag':
					gridEditor.setColumnWidth(idx, 250);
					break;
				case 'textarea':
					gridEditor.setColumnWidth(idx, 350);
					break;
			}
		});
	};

/** Update column properties from schema */
const updateSchema = (gridEditor: GridEditor) => (schema: ColumnSchema) => {
	const {minimumColumnWidth} = gridEditor.properties;
	schema.forEach((colProps, idx) => {
		gridEditor.setColumnProperties(
			idx,
			{..._.omit(colProps, 'name', 'header')},
			{minimumColumnWidth},
		);
	});
	setWidths(gridEditor)();
};

/** Setup hypergrid columns */
export default function columns(plugin: GridEditor) {
	const _getWidth = getWidth(plugin);
	const _setWidth = setWidth(plugin);
	const _setWidths = setWidths(plugin);
	const _updateSchema = updateSchema(plugin);

	plugin.behavior.getColumnWidth = _getWidth;
	plugin.setColumnWidth = _setWidth;

	return {
		getWidth: _getWidth,
		setWidth: _setWidth,
		setWidths: _setWidths,
		updateSchema: _updateSchema,
	};
}
